import React, { useState, useEffect, useRef } from 'react';
import { isIOS } from './utils/platform';
import { ConditionalRoute } from './components/ConditionalRoute';
import { SplashScreen } from '@capacitor/splash-screen';
import { setupStatusBar, updateStatusBarStyle } from './utils/statusBar';
import { Routes, Route, useNavigate, Link, Navigate } from 'react-router-dom';
import { LogOut, Shield, MessageCircle, ChevronDown, Bell, AlertTriangle, Crown, BarChart2, User, Target, Settings as SettingsIcon, UserX } from 'lucide-react';
import Write from './components/Write';
import Practice from './components/Practice';
import Goals from './components/Goals';
import IdeaBank from './components/IdeaBank';
import AdminPortal from './components/AdminPortal';
import Login from './components/Login';
import Profile from './components/Profile';
import { Feed, Post } from './components/feed/Feed';
import ModernFeed from './components/feed/ModernFeed';
import Exercises from './components/Exercises';
import FeedbackModal from './components/modals/FeedbackModal';
import NotificationsModal from './components/modals/NotificationsModal';
import CommentModal from './components/modals/CommentModal';
import { useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import logoImage from './assets/logo.png';
import Navigation from './components/Navigation';
import Layout from './components/Layout';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import { db } from './lib/firebase';
import { query, collection, where, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { NotificationProvider } from './contexts/NotificationContext';
import MobileNavigation from './components/MobileNavigation';
import { MobileMenuProvider } from './contexts/MobileMenuContext';
import Analytics from './components/Analytics';
import { UserProvider } from './contexts/UserContext';
import { usePageTracking } from './hooks/usePageTracking';
import Membership from './components/Membership';
import CheckoutSuccess from './pages/checkout/Success';
import CheckoutCancel from './pages/checkout/Cancel';
import SetList from './components/SetList';
import { getDoc, doc } from 'firebase/firestore';
import './styles/highlight.css';
import Settings from './components/Settings';
import BlockedUsersPage from './pages/BlockedUsersPage';
import EULA from './components/legal/EULA';
import DailyWordExercise from './pages/DailyWordExercise';

export default function App() {
  const { theme } = useTheme();
  
  useEffect(() => {
    setupStatusBar();
    updateStatusBarStyle(theme === 'dark');
    // Hide the splash screen with a fade animation
    SplashScreen.hide({
      fadeOutDuration: 500
    });
  }, [theme]);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);

  const onSelectPost = async (postId: string, commentId: string | null) => {
    try {
      console.log("onSelectPost called:", { postId, commentId });

      const postDoc = await getDoc(doc(db, 'posts', postId));
      if (postDoc.exists()) {
        console.log("Post found, setting state:", postDoc.data());
        setSelectedPost({ id: postDoc.id, ...postDoc.data() } as Post);
        setSelectedCommentId(commentId);
        // Navigate to the feed page with postId in URL for scrolling
        if (commentId) {
          // For comments, we still want to show the modal
          navigate('/');
        } else {
          // For likes, we want to scroll to the post
          navigate(`/?postId=${postId}`);
        }
      } else {
        console.warn("Post not found in Firestore.");
      }
    } catch (error) {
      console.error("Error fetching post:", error);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        dropdownButtonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !dropdownButtonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { user, logout, isBanned, isAdmin } = useAuth();
  const { toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Track page views
  usePageTracking();

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'notifications'),
      where('toUserId', '==', user.uid),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadCount(snapshot.size);
    });

    return () => unsubscribe();
  }, [user]);

  const handleProfileClick = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  if (isBanned) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <div className="bg-red-900 p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-red-300" />
          <h1 className="text-2xl font-bold text-white mb-4">Account Suspended</h1>
          <p className="text-red-200 mb-6">
            Your account has been suspended due to a violation of our community guidelines.
            If you believe this is an error, please contact our support team.
          </p>
          <button
            onClick={() => logout()}
            className="bg-red-700 hover:bg-red-800 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <Login />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <NotificationProvider>
        <MobileMenuProvider>
          <UserProvider>
            <div className={`min-h-screen ${theme === 'dark' ? 'bg-navy-900' : 'bg-[#EAEDED]'} flex flex-col transition-colors duration-200`}>
              <header className={`${theme === 'dark' ? 'bg-navy-900' : 'bg-white'} shadow-md border-b-[3px] border-orange-500 transition-colors duration-200 pt-[calc(max(env(safe-area-inset-top),_24px)_+_1rem)] pb-4`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center h-24 md:h-[120px]">
                    <div className="block lg:hidden">
                      <MobileNavigation />
                    </div>
                    <div className="w-[200px] md:w-[140px] ml-4">
                      <Link to="/" className="cursor-pointer">
                        <img
                          src={logoImage}
                          alt="Home"
                          className="h-[80px] md:h-[120px] w-[80px] md:w-[120px] object-contain"
                        />
                      </Link>
                    </div>
                    <div className="flex-1 flex justify-center">
                      <Navigation />
                    </div>
                    <div className="w-[200px] md:w-[140px] flex items-center justify-end gap-4">
                      {user && (
                        <>
                          <button
                            ref={notificationButtonRef}
                            className={`relative p-2 rounded-full ${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'} hover:bg-black/5 transition-colors`}
                            onClick={() => setShowNotifications(!showNotifications)}
                          >
                            <Bell size={28} />
                            {unreadCount > 0 && (
                              <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs w-6 h-6 flex items-center justify-center rounded-full">
                                {unreadCount}
                              </span>
                            )}
                          </button>
                        </>
                      )}
                      <div className="relative">
                        <button
                          ref={dropdownButtonRef}
                          onClick={() => setShowDropdown(!showDropdown)}
                          className="flex items-center"
                        >
                          <img
                            src={user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.uid}`}
                            alt=""
                            className="h-12 w-12 rounded-full"
                          />
                          <div className="pl-4">
                            <ChevronDown size={20} className={`transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
                          </div>
                        </button>
                        {showDropdown && (
                          <div 
                            ref={dropdownRef}
                            className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg ${theme === 'dark' ? 'bg-navy-800' : 'bg-white'} ring-1 ring-black ring-opacity-5 z-50`}
                          >
                            <div className="py-1" role="menu" aria-orientation="vertical">
                              <button
                                onClick={() => {
                                  navigate(`/profile/${user.uid}`);
                                  setShowDropdown(false);
                                }}
                                className={`flex items-center w-full px-4 py-2 text-sm ${theme === 'dark' ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`}
                                role="menuitem"
                              >
                                <User size={16} className="mr-2" />
                                Profile
                              </button>
                              <button
                                onClick={() => {
                                  setShowFeedbackModal(true);
                                  setShowDropdown(false);
                                }}
                                className={`flex items-center w-full px-4 py-2 text-sm ${theme === 'dark' ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`}
                                role="menuitem"
                              >
                                <MessageCircle size={16} className="mr-2" />
                                Feedback
                              </button>
                              <button
                                onClick={() => {
                                  navigate('/analytics');
                                  setShowDropdown(false);
                                }}
                                className={`flex items-center w-full px-4 py-2 text-sm ${theme === 'dark' ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`}
                                role="menuitem"
                              >
                                <BarChart2 size={16} className="mr-2" />
                                Analytics
                              </button>
                              <button
                                onClick={() => {
                                  navigate('/goals');
                                  setShowDropdown(false);
                                }}
                                className={`flex items-center w-full px-4 py-2 text-sm ${theme === 'dark' ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`}
                                role="menuitem"
                              >
                                <Target size={16} className="mr-2" />
                                Goals
                              </button>
                              <button
                                onClick={() => {
                                  navigate('/settings');
                                  setShowDropdown(false);
                                }}
                                className={`flex items-center w-full px-4 py-2 text-sm ${theme === 'dark' ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`}
                                role="menuitem"
                              >
                                <SettingsIcon size={16} className="mr-2" />
                                Settings
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={() => {
                                    navigate('/admin');
                                    setShowDropdown(false);
                                  }}
                                  className={`flex items-center w-full px-4 py-2 text-sm ${theme === 'dark' ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`}
                                  role="menuitem"
                                >
                                  <Shield size={16} className="mr-2" />
                                  Admin
                                </button>
                              )}
                              <Link
                                to="/blocked-users"
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => setShowDropdown(false)}
                              >
                                <UserX size={16} className="mr-2" />
                                Blocked Users
                              </Link>
                              <button
                                onClick={() => {
                                  logout();
                                  setShowDropdown(false);
                                }}
                                className={`flex items-center w-full px-4 py-2 text-sm ${theme === 'dark' ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`}
                                role="menuitem"
                              >
                                <LogOut size={16} className="mr-2" />
                                Logout
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </header>

              <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />
              <NotificationsModal
                isOpen={showNotifications}
                onClose={() => setShowNotifications(false)}
                anchorRef={notificationButtonRef}
                onSelectPost={onSelectPost}
              />
              {/* Only show CommentModal if we have both a post and a comment ID */}
              {selectedPost && selectedCommentId && (
                <CommentModal 
                  isOpen={true}
                  onClose={() => {
                    setSelectedPost(null);
                    setSelectedCommentId(null);
                  }}
                  post={selectedPost}
                  highlightedCommentId={selectedCommentId}
                />
              )}

              <main className="flex-grow container mx-auto px-4 py-8">
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/" element={
                    <Layout>
                      <ModernFeed />
                    </Layout>
                  } />
                  <Route path="/community" element={
                    <Layout>
                      <Feed onProfileClick={handleProfileClick} />
                    </Layout>
                  } />
                  <Route path="/feed" element={<Navigate to="/community" replace />} />
                  <Route path="/write" element={
                    <Layout>
                      <Write />
                    </Layout>
                  } />
                  <Route path="/write/:bitId" element={
                    <Layout>
                      <Write />
                    </Layout>
                  } />
                  <Route path="/practice" element={
                    <Layout>
                      <Practice />
                    </Layout>
                  } />
                  <Route path="/exercises" element={
                    <Layout>
                      <Exercises />
                    </Layout>
                  } />
                  <Route path="/goals" element={
                    <Layout>
                      <Goals />
                    </Layout>
                  } />
                  <Route path="/settings" element={
                    <Layout>
                      <Settings />
                    </Layout>
                  } />
                  <Route path="/ideas" element={
                    <Layout>
                      <IdeaBank />
                    </Layout>
                  } />
                  <Route path="/profile/:userId" element={
                    <Layout>
                      <Profile />
                    </Layout>
                  } />
                  <Route path="/admin" element={
                    <Layout>
                      {isAdmin ? <AdminPortal /> : <Feed onProfileClick={handleProfileClick} />}
                    </Layout>
                  } />
                  <Route path="/privacy" element={
                    <Layout>
                      <Privacy />
                    </Layout>
                  } />
                  <Route path="/membership" element={
                    <Layout>
                      <Membership />
                    </Layout>
                  } />
                  <Route path="/analytics" element={
                    <Layout>
                      <Analytics />
                    </Layout>
                  } />
                  <Route path="/setlist" element={
                    <Layout>
                      <SetList />
                    </Layout>
                  } />

                  <Route path="/checkout/success" element={
                    <Layout>
                      <CheckoutSuccess />
                    </Layout>
                  } />
                  <Route path="/checkout/cancel" element={
                    <Layout>
                      <CheckoutCancel />
                    </Layout>
                  } />
                  <Route path="/blocked-users" element={
                    <Layout>
                      <BlockedUsersPage />
                    </Layout>
                  } />
                  <Route path="/eula" element={
                    <Layout>
                      <EULA />
                    </Layout>
                  } />
                  <Route path="/exercise/daily-word" element={
                    <Layout>
                      <DailyWordExercise />
                    </Layout>
                  } />
                </Routes>
              </main>
            </div>
          </UserProvider>
        </MobileMenuProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}